import { Fragment, type ReactNode } from 'react';
import { Link } from './i18n';

/**
 * A tiny, safe Markdown subset renderer (## headings, paragraphs, - lists, **bold**, [links](/x)).
 * It builds React elements — never raw HTML — so admin-authored content cannot inject scripts.
 */
function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)\s]+)\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[2]) out.push(<strong key={`${keyBase}-b${i++}`}>{m[2]}</strong>);
    else if (m[4] && m[5]) {
      const href = m[5];
      if (href.startsWith('/')) out.push(<Link key={`${keyBase}-l${i++}`} to={href}>{m[4]}</Link>);
      else if (/^(https?:|mailto:|tel:)/.test(href)) out.push(<a key={`${keyBase}-l${i++}`} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">{m[4]}</a>);
      else out.push(m[4]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ source, className = 'prose-shop' }: { source: string; className?: string }) {
  const blocks = source.replace(/\r\n/g, '\n').split(/\n{2,}/);
  return (
    <div className={className}>
      {blocks.map((block, bi) => {
        const lines = block.split('\n').filter((l) => l.trim() !== '');
        if (!lines.length) return null;
        if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
          return (
            <ul key={bi}>
              {lines.map((l, li) => (
                <li key={li}>{inline(l.replace(/^\s*[-*]\s+/, ''), `${bi}-${li}`)}</li>
              ))}
            </ul>
          );
        }
        if (/^###\s/.test(lines[0])) return <h3 key={bi}>{inline(lines[0].slice(4), `${bi}`)}</h3>;
        if (/^##?\s/.test(lines[0])) {
          const rest = lines.slice(1).join(' ');
          return (
            <Fragment key={bi}>
              <h2>{inline(lines[0].replace(/^##?\s/, ''), `${bi}h`)}</h2>
              {rest && <p>{inline(rest, `${bi}p`)}</p>}
            </Fragment>
          );
        }
        return <p key={bi}>{inline(lines.join(' '), `${bi}`)}</p>;
      })}
    </div>
  );
}
